import React, { useState, useEffect } from 'react';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { useAuth0 } from '@auth0/auth0-react';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Slide from '@mui/material/Slide';
import Fade from '@mui/material/Fade';
import { globalConfig } from './settings/global.config';
import authService from './services/auth.service';
import { instance } from './helpers/settings';
import { getBrowser, hash } from './helpers/functions';
import { locales } from './helpers/constants';

let Routes = null;
let store = null;
let persistor = null;

window.isIEorEdgeHTML = getBrowser();

const allRoutes = [];

const App = () => {
	const [message, setMessage] = useState(undefined);
	const [appIsLoaded, setAppIsLoaded] = useState(false);
	const [isAppLoading, setIsLoading] = useState(true);
	const [isLoadingIconVisible, setIsLoadingIconVisible] = useState(true);
	const { user, isLoading, isAuthenticated, loginWithRedirect, getAccessTokenSilently } = useAuth0();

	useEffect(() => {
		if (window.regionalAppNames !== null && window.regionalAppNames !== undefined) {
			document.title = window.regionalAppNames[globalConfig.configuration.environment.region];
		}
	}, [window.regionalAppNames]);

	const errorHappened = m => {
		setIsLoadingIconVisible(false);
		setMessage(m);
	};

	const authenticateProductsPlatform = () =>
		authService
			.authenticate(isAuthenticated, getAccessTokenSilently)
			.then(() => new Promise((resolve, reject) => resolve()))
			.catch(error => {
				errorHappened('Could not log in to Reporting Platform');

				return new Promise((resolve, reject) => reject(error));
			});

	const getInitialState = () =>
		instance({
			method: 'post',
			url: 'System/Authorization',
			contentType: 'application/json',
			data: null,
			responseType: 'json'
		})
			.then(response => {
				window.VisibleOrgUnits = response.data.units;
				window.metrics = response.data.metrics;
				window.metricsList = response.data.metricsList;
				window.grades = response.data.grades;
				window.IsRosterPerformUser = response.data.isRpUser;
				window.IsInReportsMode = response.data.isReportsUser;
				window.userSettings = response.data.user;
				window.regionalAppNames = response.data.regionalAppNames;
				window.rosterCycleDate = response.data.rosterCycleDate;
				window.rosterCycleLength = response.data.rosterCycleLength;
				window.lastRosterCycleDateFrom = response.data.lastRosterCycleDateFrom;
				window.lastRosterCycleDateTo = response.data.lastRosterCycleDateTo;
				window.currentRosterCycleDateFrom = response.data.currentRosterCycleDateFrom;
				window.currentRosterCycleDateTo = response.data.currentRosterCycleDateTo;
				window.nextRosterCycleDateFrom = response.data.nextRosterCycleDateFrom;
				window.nextRosterCycleDateTo = response.data.nextRosterCycleDateTo;

				if (response.data.isReportsUser) {
					window.IsInReportsMode = 'True';
				}

				if (window.IsRosterPerformUser) {
					allRoutes.push(
						{
							path: '/metrics',
							label: 'areas_Metrics',
							exact: false,
							id: 'navMetrics',
							index: 0
						},
						{
							path: '/analysis',
							label: 'areas_Analysis',
							exact: false,
							id: 'navAnalyses',
							index: 1
						}
					);

					if (window.userSettings.roles.includes(0)) {
						allRoutes.push({
							path: '/administration',
							label: 'areas_Administration',
							exact: false,
							id: 'navAdmin',
							index: 2
						});
					}
				}

				if (window.IsInReportsMode) {
					allRoutes.splice(
						window.userSettings.roles.includes(0) ? allRoutes.length - 1 : allRoutes.length,
						0,
						{
							path: '/reports',
							label: 'areas_Reports',
							exact: false,
							id: 'navReports',
							index: window.IsRosterPerformUser ? 2 : 0
						}
					);

					if (window.IsRosterPerformUser && window.userSettings.roles.includes(0)) {
						allRoutes[allRoutes.length - 1].index = 3;
					}
				}

				return new Promise((resolve, reject) => resolve());
			})
			.catch(error => {
				errorHappened(error.response.data);

				return new Promise((resolve, reject) => reject(error));
			});

	const initiateReduxStore = () =>
		import('./store/configureStore')
			.then(module => {
				store = module.default.store;
				persistor = module.default.persistor;

				return new Promise((resolve, reject) => resolve());
			})
			.catch(error => {
				errorHappened(
					'Could not initiate the local system memory. Either the configuration of the org. unit structure is not correct or there is an issue with the local memory of the system. Please contact your administrator for further assistance.'
				);

				return new Promise((resolve, reject) => reject(error));
			});

	const initializeLocalization = () =>
		import('./helpers/i18n')
			.then(module => {
				const i18n = module.default;
				const matchingLocale = locales.find(locale => locale.languageCode === window.userSettings.locale);

				if (matchingLocale.localeKey !== i18n.language) {
					i18n.changeLanguage(matchingLocale.localeKey);
				}

				return new Promise((resolve, reject) => resolve());
			})
			.catch(error => {
				errorHappened('Language resources failed to load.');

				return new Promise((resolve, reject) => reject(error));
			});

	const importRoutes = () =>
		import('./components/areas/layout/Routing')
			.then(module => {
				Routes = module.default;

				return new Promise((resolve, reject) => resolve());
			})
			.catch(error => {
				errorHappened('Application failed to load.');

				return new Promise((resolve, reject) => reject(error));
			});

	const loadSystem = () => {
		authenticateProductsPlatform().then(response => {
			getInitialState(response).then(() => {
				initiateReduxStore().then(() => {
					initializeLocalization().then(() => {
						importRoutes().then(() => {
							setMessage('Welcome');
							setIsLoadingIconVisible(false);
							setIsLoading(false);
							setAppIsLoaded(true);
						});
					});
				});
			});
		});
	};

	const startPendo = u => {
		const mail = hash(u.email, 0);

		pendo.initialize({
			visitor: {
				id: `RP11-${u['http://schemas.allocatesoftware.com/v1/identity/claims/primary_trust']}-${mail}`
			},
			account: {
				id: u['http://schemas.allocatesoftware.com/v1/identity/claims/primary_trust'],
				module: 'RP11'
			}
		});
	};

	useEffect(() => {
		if (!isLoading && !isAuthenticated) {
			loginWithRedirect({
				appState: {
					returnTo: window.location.pathname
				}
			});
		} else if (!isLoading && isAuthenticated) {
			loadSystem();
			startPendo(user);
		}
	}, [isLoading, isAuthenticated, user]);

	return (
		<div>
			<Slide direction="down" in={isAppLoading} mountOnEnter unmountOnExit>
				<Backdrop open sx={{ backgroundColor: '#4339E5', color: 'white' }}>
					<Grid container justifyContent="center" textAlign="center">
						<Grid item xs={12} sm={6} md={6} lg={3} sx={{ ml: 3, mr: 3 }}>
							<img
								style={{ minWidth: '350px' }}
								src={`./images/logo-${globalConfig.configuration.environment.region}-fr-white.svg`}
								alt=""
							/>
						</Grid>
						{isLoadingIconVisible && (
							<Grid item xs={12} sx={{ textAlign: 'center' }}>
								<CircularProgress color="inherit" />
							</Grid>
						)}
						{message !== undefined && (
							<Grid item xs={12} sx={{ textAlign: 'center' }}>
								<Typography variant="h6">{message}</Typography>
							</Grid>
						)}
					</Grid>
				</Backdrop>
			</Slide>
			{appIsLoaded && (
				<Fade in={appIsLoaded} timeout={1500}>
					<div>
						<Provider store={store}>
							<PersistGate loading={null} persistor={persistor}>
								<Routes admin={window.userSettings.roles.includes(0)} routes={allRoutes} />
							</PersistGate>
						</Provider>
					</div>
				</Fade>
			)}
		</div>
	);
};

export default App;
